import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';

import { BaseService } from './base.service';
import { Observable, of, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginStatus } from './login-status-service';
import { LicenceValidityDTO } from './licence';
import { loginError } from '../login-form/loginError';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { HubService, isProvisionedEndpoint } from '../../home/hub.service';

// Audit Trail product identifier in the Zeidman licensing system (fa-imp-licensing).
// See https://github.com/zeiddev/... (Licence.cs) for the full product list.
const AUDIT_TRAIL_PRODUCT_ID = 225;

interface AuthUserInfo {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  orgId: string;
  orgName: string;
  userLevel: number;
}

interface AuthLoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthUserInfo;
}

interface BillingLicence {
  id: number;
  organisationId: number;
  organisationName: string;
  serialNumber: string;
  environmentId?: string | null;
  productId: number;
  registrationCode?: string | null;
  expires?: string | null;
  lifetime: boolean;
  isTrial: boolean;
  dateAdded: string;
  updated: string;
  activationCount: number;
  note?: string | null;
}

@Injectable({ providedIn: 'root' })
export class UserService extends BaseService {

  private readonly authUrl: string = environment.authURL;
  private readonly billingUrl: string = environment.billingURL;
  private readonly billingApiKey: string = environment.billingApiKey;

  private http = inject(HttpClient);
  private loginStatus = inject(LoginStatus);
  private _Hub = inject(HubService);

  constructor() {
    super();
  }


  // Uses the username and password to login. If there is a login error then the message is returned as an error within the observable. If it logs in
  // it then checks the licence. If there is an invalid licence it returns the text to display. Otherwise it returns blank.
  login(userName: string, password: string): Observable<loginError> {

    const errorReason: loginError = new loginError();

    return this.http.post<AuthLoginResponse>(
      this.authUrl + 'auth/login',
      { email: userName, password }
    ).pipe(
      tap(response => this.storeSession(response)),
      mergeMap(response => this.checkLicence(response.user.orgId).pipe(
        map(licence => ({ response, licence }))
      )),
      map(({ response, licence }) => {
        if (licence.hasLicence && !licence.licenceExpired) {
          // loginStatus is flipped by the caller once the downstream
          // config/org-details calls have completed and we're about to
          // navigate — flipping it here would show the logged-in navbar
          // while the login form is still the active route.
          errorReason.expires = licence.expires;
          errorReason.orgName = licence.organisationName || response.user.orgName;
          errorReason.serialNumber = licence.serialNumber;
          errorReason.isValid = true;
          return errorReason;
        }

        // Authentication succeeded but the licence is missing / expired —
        // roll back the session so the user doesn't appear logged in.
        this.clearSession();
        if (licence.licenceExpired) {
          errorReason.hasExpired = true;
          errorReason.expires = licence.expires;
          errorReason.orgName = licence.organisationName || response.user.orgName;
          errorReason.serialNumber = licence.serialNumber;
        } else {
          errorReason.noLicence = true;
        }
        return errorReason;
      }),
      catchError((err: HttpErrorResponse) => {
        this.clearSession();
        // fa-zz-auth returns { error: "..." } on 401 — reshape to the error_description
        // envelope the login-form component already knows how to render.
        if (err?.error && typeof err.error.error === 'string' && !err.error.error_description) {
          err.error.error_description = err.error.error;
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Refreshes the access token using the stored refresh token. Returns the new
   * access token on success, or null if no refresh token is stored / the
   * refresh call failed. On failure the local session is cleared.
   */
  refresh(): Observable<string | null> {
    const refreshToken = sessionStorage.getItem('refreshToken');
    if (!refreshToken) {
      return of(null);
    }

    return this.http.post<AuthLoginResponse>(
      this.authUrl + 'auth/refresh',
      { refreshToken }
    ).pipe(
      tap(response => this.storeSession(response)),
      map(response => response.accessToken),
      catchError(() => {
        this.clearSession();
        return of(null);
      })
    );
  }


  hasConfigBeenRun(): Observable<boolean> {
    // Only a response carrying a real endpoint counts as provisioned. The
    // previous check tested for the literal string 'error', but ZDHub reports
    // a missing org as a JSON envelope ({success:false, message:...}) with
    // HTTP 200 — which is truthy and not 'error', so a tenant with no config
    // was reported as configured and the failure surfaced later as a vague
    // message from a different step.
    return this._Hub.getOrgStackURL().pipe(map(orgUrl => isProvisionedEndpoint(orgUrl)));
  }


  getOrgDetails(): Observable<boolean> {

    return this._Hub.getOrgDetails().pipe(map(details => {
      sessionStorage.setItem('envId', details.environment_id);
      sessionStorage.setItem('envName', details.environment_name);
      return true;
    }));

  }

  logout() {
    this.clearSession();
    this.loginStatus.setStatus(false);
  }


  getAccessToken() {
    return sessionStorage.getItem('accessToken');
  }

  getRefreshToken() {
    return sessionStorage.getItem('refreshToken');
  }


  /**
   * Queries the Zeidman licensing service (fa-imp-licensing) for the given
   * organisation and returns whether a valid Audit Trail licence exists.
   * A licence is valid when productId === AUDIT_TRAIL_PRODUCT_ID AND
   * (lifetime === true OR expires is in the future).
   */
  checkLicence(orgId: string): Observable<LicenceValidityDTO> {
    if (!orgId) {
      return of(this.buildMissingLicence());
    }

    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('x-api-key', this.billingApiKey);

    return this.http
      .get<BillingLicence[]>(`${this.billingUrl}org/${orgId}/licences`, { headers })
      .pipe(
        map(licences => this.evaluateLicences(licences ?? [])),
        catchError(() => of(this.buildMissingLicence()))
      );
  }

  private evaluateLicences(licences: BillingLicence[]): LicenceValidityDTO {
    const auditLicences = licences.filter(l => l.productId === AUDIT_TRAIL_PRODUCT_ID);
    if (auditLicences.length === 0) {
      return this.buildMissingLicence();
    }

    const now = Date.now();
    // A null/missing expires is treated as non-expiring (lifetime-equivalent) —
    // some legacy rows have lifetime=false with a null expiry and should not be
    // reported as expired.
    const active = auditLicences.find(l => l.lifetime || !l.expires || this.isInFuture(l.expires, now));
    if (active) {
      return this.buildActiveLicence(active);
    }

    // All Audit Trail licences have expired — surface the most recent one so
    // the UI can display the serial number / expiry date.
    const mostRecent = auditLicences
      .slice()
      .sort((a, b) => this.parseDateMs(b.expires) - this.parseDateMs(a.expires))[0];
    return this.buildExpiredLicence(mostRecent);
  }

  private buildActiveLicence(l: BillingLicence): LicenceValidityDTO {
    const dto = new LicenceValidityDTO();
    dto.hasLicence = true;
    dto.licenceExpired = false;
    dto.expires = l.lifetime ? null! : (l.expires ? new Date(l.expires) : null!);
    dto.organisationName = l.organisationName;
    dto.serialNumber = l.serialNumber;
    return dto;
  }

  private buildExpiredLicence(l: BillingLicence): LicenceValidityDTO {
    const dto = new LicenceValidityDTO();
    dto.hasLicence = true;
    dto.licenceExpired = true;
    dto.expires = l.expires ? new Date(l.expires) : null!;
    dto.organisationName = l.organisationName;
    dto.serialNumber = l.serialNumber;
    return dto;
  }

  private buildMissingLicence(): LicenceValidityDTO {
    const dto = new LicenceValidityDTO();
    dto.hasLicence = false;
    dto.licenceExpired = false;
    return dto;
  }

  private isInFuture(dateStr: string | null | undefined, now: number): boolean {
    if (!dateStr) return false;
    const parsed = Date.parse(dateStr);
    return !isNaN(parsed) && parsed > now;
  }

  private parseDateMs(dateStr: string | null | undefined): number {
    if (!dateStr) return 0;
    const parsed = Date.parse(dateStr);
    return isNaN(parsed) ? 0 : parsed;
  }

  private storeSession(response: AuthLoginResponse): void {
    sessionStorage.setItem('accessToken', response.accessToken);
    sessionStorage.setItem('refreshToken', response.refreshToken);
    sessionStorage.setItem('userName', response.user.email);
    sessionStorage.setItem('firstName', response.user.firstName);
    sessionStorage.setItem('lastName', response.user.lastName);
    sessionStorage.setItem('locale', 'en-US');
    sessionStorage.setItem('orgId', response.user.orgId);
    sessionStorage.setItem('orgName', response.user.orgName);
  }

  private clearSession(): void {
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
  }

}
